import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Switch } from '../ui/switch';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { GET_RESEARCH_SETTINGS, UPSERT_RESEARCH_SETTINGS } from '../../graphql';
import { GetResearchSettingsResponse, UpsertResearchSettingsInput } from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';

const inputClassName =
  'w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

const PAGE_TITLE = 'Research';
const PAGE_DESCRIPTION =
  'Configure the AI provider used for answer synthesis and turn Research on or off for this account.';

function PageHeader() {
  return (
    <div className="flex-shrink-0 mb-6">
      <h2 className="mb-1">{PAGE_TITLE}</h2>
      <p className="text-muted-foreground">{PAGE_DESCRIPTION}</p>
    </div>
  );
}

export function ResearchSettings() {
  const [openaiProvider, setOpenaiProvider] = useState<'platform' | 'custom'>('platform');
  const [openaiEndpoint, setOpenaiEndpoint] = useState<string>('');
  const [openaiApiKey, setOpenaiApiKey] = useState<string>('');
  // The server never returns a saved key, only '***' to say there is one.
  const [keySaved, setKeySaved] = useState(false);
  const [openaiModel, setOpenaiModel] = useState<string>('gpt-4o-mini');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [saveMessage, setSaveMessage] = useState<string>('');

  const { accountId, isLoading: accountsLoading } = useAccount();

  const {
    loading: settingsLoading,
    error,
    data,
    refetch,
  } = useQuery<GetResearchSettingsResponse>(GET_RESEARCH_SETTINGS, {
    variables: { accountId },
    skip: !accountId,
  });

  const [upsertResearchSettings, { loading: saving }] = useMutation(UPSERT_RESEARCH_SETTINGS, {
    onCompleted: () => {
      refetch();
      setSaveMessage('Settings saved.');
    },
    onError: (err) => {
      console.error('Error saving research settings:', err);
      setSaveMessage(`Failed to save: ${err.message}`);
    },
  });

  useEffect(() => {
    const settings = data?.researchSettings;
    if (settings) {
      setOpenaiProvider(settings.openaiProvider === 'custom' ? 'custom' : 'platform');
      setOpenaiEndpoint(settings.openaiEndpoint || '');
      setKeySaved(Boolean(settings.openaiApiKey));
      setOpenaiApiKey('');
      setOpenaiModel(settings.openaiModel || 'gpt-4o-mini');
      setIsActive(settings.isActive);
    }
  }, [data]);

  const handleSave = async () => {
    if (!accountId) return;
    setSaveMessage('');

    const input: UpsertResearchSettingsInput = {
      openaiProvider,
      openaiEndpoint: openaiEndpoint.trim() || undefined,
      openaiApiKey: openaiApiKey.trim() || undefined,
      openaiModel: openaiModel.trim() || undefined,
      isActive,
    };

    await upsertResearchSettings({ variables: { accountId, input } });
  };

  const customIncomplete =
    openaiProvider === 'custom' && (!openaiEndpoint.trim() || (!openaiApiKey.trim() && !keySaved));

  if (accountsLoading || settingsLoading) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader />
        <Card className="p-6">
          <Loader label="Loading" className="text-cmp-ink" />
        </Card>
      </div>
    );
  }

  if (error) {
    console.error(error);
    return (
      <div className="flex flex-col h-full">
        <PageHeader />
        <Card className="p-6">
          <p className="text-error">Error loading research settings</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader />

      <div className="space-y-6 overflow-auto">
        <Card className="p-6 bg-core-dark border-0">
          <h3 className="text-cmp-ink mb-1">AI Provider</h3>
          <p className="text-muted-foreground text-sm mb-4">
            Used for answer synthesis and follow-up question generation.
          </p>

          <RadioGroup
            value={openaiProvider}
            onValueChange={(value) => setOpenaiProvider(value as 'platform' | 'custom')}
            className="gap-3"
          >
            <label
              htmlFor="research-provider-platform"
              className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                openaiProvider === 'platform'
                  ? 'border-primary bg-form-dark'
                  : 'border-cmp-line-2 hover:border-cmp-line-2'
              }`}
            >
              <RadioGroupItem value="platform" id="research-provider-platform" className="mt-0.5" />
              <div>
                <div className="text-cmp-ink text-sm font-medium">Platform AI (default)</div>
                <p className="text-muted-foreground text-sm mt-0.5">
                  Use our managed AI endpoint and language models. No setup required.
                </p>
              </div>
            </label>

            <label
              htmlFor="research-provider-custom"
              className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                openaiProvider === 'custom'
                  ? 'border-primary bg-form-dark'
                  : 'border-cmp-line-2 hover:border-cmp-line-2'
              }`}
            >
              <RadioGroupItem value="custom" id="research-provider-custom" className="mt-0.5" />
              <div>
                <div className="text-cmp-ink text-sm font-medium">Bring your own</div>
                <p className="text-muted-foreground text-sm mt-0.5">
                  Use your own Azure AI Foundry / Azure OpenAI deployment with your key, endpoint,
                  and model choice. Requests go only to your endpoint.
                </p>
              </div>
            </label>
          </RadioGroup>

          {openaiProvider === 'custom' && (
            <div className="space-y-4 mt-4 pt-4 border-t border-cmp-line">
              <div className="space-y-2">
                <Label htmlFor="research-openai-endpoint">Endpoint</Label>
                <Input
                  id="research-openai-endpoint"
                  placeholder="https://example.openai.azure.com"
                  value={openaiEndpoint}
                  onChange={(e) => setOpenaiEndpoint(e.target.value)}
                  className={inputClassName}
                />
                <p className="text-muted-foreground text-xs">
                  Paste your Azure resource endpoint, or a full chat-completions URL including
                  api-version if your deployment requires one.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="research-openai-key">API key</Label>
                  <Input
                    id="research-openai-key"
                    type="password"
                    placeholder={keySaved ? 'Saved. Leave blank to keep it' : 'API key'}
                    value={openaiApiKey}
                    onChange={(e) => setOpenaiApiKey(e.target.value)}
                    className={inputClassName}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="research-openai-model">Model or deployment name</Label>
                  <Input
                    id="research-openai-model"
                    placeholder="e.g. gpt-4o-mini"
                    value={openaiModel}
                    onChange={(e) => setOpenaiModel(e.target.value)}
                    className={inputClassName}
                  />
                </div>
              </div>

              {customIncomplete && (
                <p className="text-warning text-sm">
                  Endpoint and API key are required to use your own deployment. Until both are
                  saved, Research will run in retrieval-only mode (no synthesized answers) — it will
                  not fall back to the platform AI.
                </p>
              )}
            </div>
          )}
        </Card>

        <Card className="p-6 bg-core-dark border-0">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="research-active">Enable Research</Label>
              <p className="text-muted-foreground text-sm mt-1">
                Turn the Research page on or off for this account.
              </p>
            </div>
            <Switch id="research-active" checked={isActive} onCheckedChange={setIsActive} />
          </div>

          <div className="pt-6 flex items-center justify-end gap-4">
            {saveMessage && (
              <p
                className={
                  saveMessage.startsWith('Failed') ? 'text-error text-sm' : 'text-success text-sm'
                }
              >
                {saveMessage}
              </p>
            )}
            <Button
              onClick={handleSave}
              disabled={saving || !accountId}
              className="bg-button-primary text-button-primary"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
